import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import InfoButton from '../../components/Common-components/infoButton';
import '../../css/Booking/guestRoomBook.css';
import { ReactComponent as Arrow } from '../../assets/icons/svg/arrowUnder.svg';

// 리스트 - 마커 매칭용 키 생성
const buildCabinKey = (c, fallbackIndex = 0) => {
  const no = String(c?.cabinno ?? '').trim();
  const loc = String(c?.location ?? '').trim();
  if (no || loc) return `${no}|${loc}`;
  return `${c?.resultno ?? 'N'}|${fallbackIndex}`;
};

// 선실번호 기준 오름차순 정렬용 비교 함수
const parseCabinNumber = (c) => {
  const digits = String(c?.cabinno ?? '').replace(/\D/g, '');
  return digits ? parseInt(digits, 10) : Number.POSITIVE_INFINITY;
};
const compareCabinNumberAsc = (a, b) => {
  const na = parseCabinNumber(a);
  const nb = parseCabinNumber(b);
  if (na !== nb) return na - nb;
  // 보조 정렬: 같은 번호면 위치명으로
  const la = String(a?.location ?? '');
  const lb = String(b?.location ?? '');
  return la.localeCompare(lb);
};

/*
  배치도 선실 오버레이
  - deck.cabins의 좌표(x1,x2,y1,y2)는 배치도 원본 이미지의 픽셀 기준 바운딩
  - 이미지 로드 후 naturalWidth/Height를 얻고, 좌표를 퍼센트(%)로 환산
    - left% = x1 / naturalWidth * 100
    - top% = y1 / naturalHeight * 100
    - width% = (x2 - x1) / naturalWidth * 100
    - height% = (y2 - y1) / naturalHeight * 100
  - 퍼센트 값을 position:absolute 박스에 적용
  - 모바일에서도 적용
*/
function DeckImageOverlay({ imageUrl, cabins = [], alt, onSelect, highlightedKey, autoScroll = false, isolateOthers = false, scrollContainerRef, targetDisplayHeight}) {
  const imgRef = useRef(null);
  const [dims, setDims] = useState({ natW: 0, natH: 0 });
  const [displaySize, setDisplaySize] = useState(null);
  const boxRefs = useRef(new Map());

  // 선택된 마커로 스크롤 (모바일에서 사용)
  useEffect(() => {
    if (!autoScroll || !highlightedKey) return;
    const el = boxRefs.current.get(highlightedKey);
    if (!el) return;

    const container = scrollContainerRef?.current;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const markerRect = el.getBoundingClientRect();
      const rawTop = markerRect.top - containerRect.top + container.scrollTop - ((container.clientHeight - markerRect.height) / 2);
      const rawLeft = markerRect.left - containerRect.left + container.scrollLeft - ((container.clientWidth - markerRect.width) / 2);

      const maxTop = Math.max(container.scrollHeight - container.clientHeight, 0);
      const maxLeft = Math.max(container.scrollWidth - container.clientWidth, 0);

      const nextTop = Number.isFinite(rawTop) ? Math.min(Math.max(rawTop, 0), maxTop) : 0;
      const nextLeft = Number.isFinite(rawLeft) ? Math.min(Math.max(rawLeft, 0), maxLeft) : 0;

      container.scrollTo({ top: nextTop, left: nextLeft, behavior: 'smooth' });
      return;
    }

    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    } catch (_) {
      // ignore
    }
  }, [highlightedKey, autoScroll, scrollContainerRef]);

  const computeDisplaySize = useCallback(() => {
    if (!targetDisplayHeight || !dims.natW || !dims.natH) return null;

    const baseWidth = dims.natW;
    const baseHeight = dims.natH;
    if (!baseWidth || !baseHeight) return null;

    const MAX_SCALE = 60;
    const desiredHeight = Math.max(targetDisplayHeight, baseHeight);
    let scale = Math.min(desiredHeight / baseHeight, MAX_SCALE);

    const containerWidth = scrollContainerRef?.current?.clientWidth ?? 0;
    if (containerWidth && baseWidth * scale > containerWidth) {
      scale = containerWidth / baseWidth;
    }

    if (!Number.isFinite(scale) || scale <= 0) return null;

    const width = Math.round(baseWidth * scale);
    const height = Math.round(baseHeight * scale);
    return { width, height };
  }, [targetDisplayHeight, dims.natW, dims.natH, scrollContainerRef]);

  useEffect(() => {
    if (!targetDisplayHeight) {
      setDisplaySize(null);
      return;
    }
    setDisplaySize(computeDisplaySize());
  }, [targetDisplayHeight, computeDisplaySize]);

  useEffect(() => {
    if (!scrollContainerRef?.current || !targetDisplayHeight || typeof ResizeObserver !== 'function') return undefined;
    const container = scrollContainerRef.current;
    const observer = new ResizeObserver(() => {
      setDisplaySize(computeDisplaySize());
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [scrollContainerRef, targetDisplayHeight, computeDisplaySize]);

  const toPercent = (val, base) => (typeof val === 'number' && base > 0) ? (val / base) * 100 : 0;

  return (
    <div className="ship-floor-stage">
      <img
        ref={imgRef}
        src={imageUrl}
        alt={alt}
        onLoad={(e) => setDims({ natW: e.target.naturalWidth, natH: e.target.naturalHeight })}
        style={displaySize ? { width: `${displaySize.width}px`, height: `${displaySize.height}px` } : undefined}
      />
      {dims.natW > 0 && dims.natH > 0 && cabins
        .filter(c => ['x1','x2','y1','y2'].every(k => typeof c?.[k] === 'number'))
        .map((cabin, i) => {
          // 원본 픽셀 좌표 → 퍼센트(%) 좌표로 변환
          const left = toPercent(cabin.x1, dims.natW);
          const top = toPercent(cabin.y1, dims.natH);
          const width = toPercent(cabin.x2 - cabin.x1, dims.natW);
          const height = toPercent(cabin.y2 - cabin.y1, dims.natH);
          const key = buildCabinKey(cabin, i);
          const hidden = isolateOthers && highlightedKey && key !== highlightedKey;
          return (
            <div
              key={key}
              className="cabin-box"
              title={`${cabin.cabinno || ''} ${cabin.location || ''}`.trim()}
              style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%`, visibility: hidden ? 'hidden' : 'visible' }}
              onClick={onSelect ? () => onSelect(cabin) : undefined}
              ref={(el) => { if (el) boxRefs.current.set(key, el); else boxRefs.current.delete(key); }}
            />
          );
        })}
    </div>
  );
}

/** 숫자만 뽑아 'nF'로 표기 */
const formatDeckName = (name) => {
  if (!name) return '';
  const num = String(name).replace(/\D/g, '');
  return num ? `${parseInt(num, 10)}F` : name;
};

const isGTYDeck = (deck) => {
  const idStr = String(deck?.id || '').toUpperCase();
  const nameStr = String(deck?.name || '').toUpperCase();
  return idStr === 'GTY' || nameStr === 'GTY';
};

/** 데크 숫자를 파싱 (없으면 -Infinity로 처리해 뒤로 밀림) */
const parseDeckNumber = (deck) => {
  const nameMatch = String(deck?.name || '').match(/\d+/);
  const idMatch = String(deck?.id || '').match(/\d+/);
  if (nameMatch) return Number(nameMatch[0]);
  if (idMatch) return Number(idMatch[0]);
  return -Infinity;
};

/** 높은 층 → 낮은 층, GTY는 항상 마지막 */
const sortDecksHighToLow = (list = []) => {
  const normalDecks = list.filter((d) => !isGTYDeck(d));
  const gtyDecks = list.filter((d) => isGTYDeck(d));
  normalDecks.sort((a, b) => {
    const numA = parseDeckNumber(a);
    const numB = parseDeckNumber(b);
    if (numA !== numB) return numB - numA; // 높은 층 우선
    return String(b.name || b.id || '').localeCompare(String(a.name || a.id || '')); // 동일 층이면 이름 역순
  });
  return [...normalDecks, ...gtyDecks];
};

/** 안전한 deckList 추출 (신/구 포맷 모두 커버) */
function extractDeckList(roomDetail) {
  const v1 = roomDetail?.carbinGradeInfo?.deckInfo?.data?.deckList; // 새 구조
  if (Array.isArray(v1)) return v1;
  const v0 = roomDetail?.carbinGradeInfo?.deckInfo?.deckList;       // 구 구조
  if (Array.isArray(v0)) return v0;
  return [];
}

function extractGuaranteed(roomDetail) {
  const v1 = roomDetail?.carbinGradeInfo?.deckInfo?.data?.guaranteed;
  if (Array.isArray(v1)) return v1;
  const v0 = roomDetail?.carbinGradeInfo?.deckInfo?.guaranteed;
  if (Array.isArray(v0)) return v0;
  return [];
}

/** 선실이 1개 이상 있는 층만 남김 (단, GTY는 객실이 없어도 포함) */
function onlyCabinDecks(list = []) {
  return list.filter(d => {
    const isGTY = String(d?.id).toUpperCase() === 'GTY' || String(d?.name).toUpperCase() === 'GTY';
    if (isGTY) return true; // GTY 탭은 객실이 비어도 노출
    return Array.isArray(d?.cabins) && d.cabins.length > 0;
  });
}

/** ---------- PC 탭 ---------- */
const CarbinTabPC = ({ data, onReserve }) => {
  const decks = onlyCabinDecks(Array.isArray(data) ? data : []);
  const firstIdStr = decks[0]?.id != null ? String(decks[0].id) : '';
  const [activeFloor, setActiveFloor] = useState(firstIdStr);
  // PC: 리스트 hover 시 해당 마커만 남기기
  const [hoveredKey, setHoveredKey] = useState(null);
  const imageScrollRef = useRef(null);
  useEffect(() => { setHoveredKey(null); }, [activeFloor]);

  useEffect(() => {
    const next = decks[0]?.id != null ? String(decks[0].id) : '';
    setActiveFloor(next);
  }, [data]); // data 바뀌면 초기화

  useEffect(() => {
    if (imageScrollRef.current) {
      imageScrollRef.current.scrollTo({ top: 0, left: 0 });
    }
  }, [activeFloor]);

  if (!decks.length) {
    return <div className="no-cabin-message">예약 가능한 선실이 없습니다.</div>;
  }

  return (
    <div className="custom-tabs-wrapper">
      <div className="custom-tab_PC">
        <div className="custom-tab">
          <div className="custom-tab-header-outer">
            <div className="custom-tab-header-wrapper">
              <div id="floor-tab" className="custom-tab-header">
                {decks.map((deck) => {
                  const idStr = String(deck.id);
                  return (
                    <button
                      key={idStr}
                      className={`tab-btn ${activeFloor === idStr ? 'active' : ''}`}
                      onClick={() => setActiveFloor(idStr)}
                    >
                      {formatDeckName(deck.name)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="custom-tab-content">
          {decks.map((deck) => {
            const idStr = String(deck.id);
            if (activeFloor !== idStr) return null;

            const cabins = Array.isArray(deck.cabins) ? deck.cabins : [];
            const sortedCabins = [...cabins].sort(compareCabinNumberAsc);
            return (
              <div key={idStr} className="floor-tab-content">
                {(() => {
                  const isGTY = String(deck?.id).toUpperCase() === 'GTY' || String(deck?.name).toUpperCase() === 'GTY';
                  const hasDeckImage = !isGTY && Boolean(deck.imageurl);
                  const shipFloorImgClass = hasDeckImage
                    ? 'ship-floor-img ship-floor-img--oversize'
                    : 'ship-floor-img';
                  return (
                    <div
                      className={shipFloorImgClass}
                      ref={hasDeckImage ? imageScrollRef : null}
                    >
                      {isGTY ? (
                        <div className="no-image-message">개런티 객실은 구조도 이미지가 제공되지 않습니다.</div>
                      ) : deck.imageurl ? (
                        // PC: 퍼센트 좌표 오버레이 + 클릭 예약 + hover 시 해당 마커만 표시
                        <DeckImageOverlay
                          imageUrl={deck.imageurl}
                          cabins={Array.isArray(deck.cabins) ? deck.cabins : []}
                          alt={deck.caption || deck.name || '배치도'}
                          onSelect={onReserve}
                          highlightedKey={hoveredKey}
                          autoScroll={Boolean(hoveredKey)}
                          isolateOthers={true}
                          scrollContainerRef={imageScrollRef}
                          targetDisplayHeight={1600}
                        />
                      ) : (
                        <div className="no-image-message">이미지가 없습니다.</div>
                      )}
                    </div>
                  );
                })()}

                <div
                  className="ship-carbin-wrapper"
                  style={(String(deck?.id).toUpperCase() === 'GTY' || String(deck?.name).toUpperCase() === 'GTY') ? { maxWidth: '100%', flex: '1 1' } : undefined}
                >
                  {sortedCabins.length > 0 ? (
                    <ul>
                      {sortedCabins.map((cabin, idx) => {
                        const isGTYDeck = String(deck?.id).toUpperCase() === 'GTY' || String(deck?.name).toUpperCase() === 'GTY';
                        const key = buildCabinKey(cabin, idx);
                        return (
                          <li
                            key={`${cabin.cabinno}-${cabin.location}-${idx}`}
                            className="ship-floor-carbin"
                            onMouseEnter={() => setHoveredKey(key)}
                            onMouseLeave={() => setHoveredKey(null)}
                          >
                            <div className="carbin-info">
                              {isGTYDeck ? (
                                <>
                                  <div className="floor" style={{display:'flex', alignItems:'center', gap:'4px'}}>
                                    개런티
                                    <InfoButton placement="top" offsetY={8}>
                                      <div style={{maxWidth: 260}}>
                                        <p style={{margin: 0, fontWeight: 600}}>개런티 객실 안내</p>
                                        <p style={{margin: '8px 0 0', fontSize: 12, lineHeight: 1.6}}>해당 등급은 개런티(선실 임의 배정) 선실입니다.<br/>최종 선실 번호는 출항 전 확정됩니다.</p>
                                      </div>
                                    </InfoButton>
                                  </div>
                                  <div className="divider" />
                                  <div className="floor-location">선실 임의 배정</div>
                                </>
                              ) : (
                                <>
                                  <div className="floor">{formatDeckName(deck.name)}</div>
                                  <div className="divider" />
                                  <div className="floor-num">
                                    <span>선실번호</span>
                                    <span>{cabin.cabinno}</span>
                                  </div>
                                  <div className="divider" />
                                  <div className="floor-location">{cabin.location}</div>
                                </>
                              )}
                            </div>
                            <button
                              type="button"
                              className="reservation-btn btn"
                              onClick={() => onReserve(cabin)}
                            >
                              예약하기
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="no-cabin-message">예약 가능한 선실이 없습니다.</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/** ---------- 모바일 ---------- */
function CarbinTabMO({ data, selectedFloor, setSelectedFloor, selectedCabin, setSelectedCabin }) {
  const floorRoomInfo = onlyCabinDecks(Array.isArray(data) ? data : []);
  const [isFloorOpen, setIsFloorOpen] = useState(false);
  const [isCabinOpen, setIsCabinOpen] = useState(false);
  const floorWrapperRef = useRef(null);
  const cabinWrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (floorWrapperRef.current && !floorWrapperRef.current.contains(e.target)) setIsFloorOpen(false);
      if (cabinWrapperRef.current && !cabinWrapperRef.current.contains(e.target)) setIsCabinOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const onFloorToggle = () => setIsFloorOpen((o) => !o);
  const onFloorSelect = (id) => {
    const idStr = String(id);
    setSelectedFloor(idStr);
    // 모바일: 층 변경 시 '모두보기'를 기본 선택으로
    setSelectedCabin({ cabinno: 'ALL', location: '모두보기' });
    setIsFloorOpen(false);
  };

  const onCabinToggle = () => setIsCabinOpen((o) => !o);
  const onCabinSelect = (cabin) => {
    setSelectedCabin(cabin);
    setIsCabinOpen(false);
  };

  const currentDeckData =
    floorRoomInfo.find((f) => String(f.id) === String(selectedFloor)) || { cabins: [] };
  const isGTYDeckMO = String(currentDeckData?.id).toUpperCase() === 'GTY' || String(currentDeckData?.name).toUpperCase() === 'GTY';
  const uniqueCabins = (currentDeckData.cabins || []).filter(
    (c, i, arr) => arr.findIndex((x) => x.cabinno === c.cabinno && x.location === c.location) === i
  );
  const uniqueCabinsSorted = [...uniqueCabins].sort(compareCabinNumberAsc);
  const moCabinOptions = [{ cabinno: 'ALL', location: '모두보기' }, ...uniqueCabinsSorted];
  const isAllSelectedMO = String(selectedCabin?.cabinno).toUpperCase() === 'ALL';
  const currentLayout = currentDeckData.imageurl;

  if (!floorRoomInfo.length) {
    return <div className="no-cabin-message">예약 가능한 선실이 없습니다.</div>;
  }

  return (
    <>
      <div className="select-tab-wrapper">
        {/* 층수 선택 */}
        <div
          className={`custom-select-wrapper${isFloorOpen ? ' open' : ''}`}
          ref={floorWrapperRef}
          onClick={onFloorToggle}
        >
          <div className="custom-select">
            {formatDeckName(currentDeckData.name) || '층 선택'}
            <Arrow className="select-arrow" />
          </div>
          <ul className={`custom-menu${isFloorOpen ? ' open' : ''}`}>
            {floorRoomInfo.map((f) => {
              const idStr = String(f.id);
              return (
                <li
                  key={idStr}
                  className={idStr === String(selectedFloor) ? 'selected' : ''}
                  onClick={(e) => {
                    e.stopPropagation();
                    onFloorSelect(idStr);
                  }}
                >
                  {formatDeckName(f.name)}
                </li>
              );
            })}
          </ul>
        </div>

        {/* 선실 선택 */}
        <div
          className={`custom-select-wrapper${isCabinOpen ? ' open' : ''}`}
          ref={cabinWrapperRef}
          onClick={onCabinToggle}
        >
          <div className="custom-select">
            {selectedCabin?.cabinno ? (
              <div className="custom-select-carbin">
                {isGTYDeckMO ? (
                  <>
                    <span>개런티</span>
                    <span className="divider" />
                    <span>선실 임의 배정</span>
                  </>
                ) : (
                  <>
                    <span>{isAllSelectedMO ? '모두보기' : selectedCabin.cabinno}</span>
                    {isAllSelectedMO ? null : (
                      <>
                        <span className="divider" />
                        <span>{selectedCabin.location}</span>
                      </>
                    )}
                  </>
                )}
              </div>
            ) : (
              '선실 선택'
            )}
            <Arrow className="select-arrow" />
          </div>
          <ul className={`custom-menu carbin${isCabinOpen ? ' open' : ''}`}>
            {moCabinOptions.map((cabin) => (
              <li
                key={`${cabin.cabinno}-${cabin.location}`}
                className={cabin.cabinno === selectedCabin?.cabinno ? 'selected' : ''}
                onClick={(e) => {
                  e.stopPropagation();
                  onCabinSelect(cabin);
                }}
              >
                {isGTYDeckMO ? (
                  <>
                    <span>개런티</span>
                    <span className="divider" />
                    <span>선실 임의 배정</span>
                  </>
                ) : (
                  <>
                    <span>{String(cabin.cabinno).toUpperCase() === 'ALL' ? '모두보기' : cabin.cabinno}</span>
                    {String(cabin.cabinno).toUpperCase() === 'ALL' ? null : (
                      <>
                        <span className="divider" />
                        <span>{cabin.location}</span>
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
            {uniqueCabinsSorted.length === 0 && (
              <li className="disabled" style={{ color: '#9AA4B2', cursor: 'not-allowed' }}>
                선택 가능한 객실 없습니다.
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* 배치도 이미지 */}
      {(() => {
        const isGTY = String(currentDeckData?.id).toUpperCase() === 'GTY' || String(currentDeckData?.name).toUpperCase() === 'GTY';
        return (
          <div className="cabin-image">
            {isGTY ? (
              <div className="no-image-message">개런티 객실은 구조도 이미지가 제공되지 않습니다.</div>
            ) : currentLayout ? (
              // MO: 전체 선택 시 전체 표시, 특정 선실 선택 시 해당 위치로 스크롤
              <DeckImageOverlay
                imageUrl={currentLayout}
                cabins={Array.isArray(currentDeckData.cabins) ? currentDeckData.cabins : []}
                alt={`${formatDeckName(currentDeckData.name)} 배치도`}
                highlightedKey={isAllSelectedMO ? undefined : buildCabinKey(selectedCabin)}
                autoScroll={!isAllSelectedMO}
                isolateOthers={!isAllSelectedMO}
              />
            ) : (
              <div className="no-image-message">이미지가 없습니다.</div>
            )}
          </div>
        );
      })()}
    </>
  );
}

/** ---------- 메인 ---------- */
function GuestRoomBook({ roomDetail, onNextStep }) {
  const [isMobile, setIsMobile] = useState(false);
  const [portalContainer, setPortalContainer] = useState(null);

  // deckList를 메모이제이션해 매 렌더마다 참조가 바뀌어 탭이 초기화 방지
  const deckListWithGTY = useMemo(() => {
    const rawDeckList = extractDeckList(roomDetail);
    // GTY는 별도 탭으로 추가하므로 원본 목록에서는 제거
    const deckList = onlyCabinDecks(rawDeckList).filter(d => !isGTYDeck(d));

    const guaranteedList = extractGuaranteed(roomDetail);
    const gtyDeck = [{
      id: 'GTY',
      name: 'GTY',
      caption: 'Guaranteed',
      imageurl: null,
      cabins: Array.isArray(guaranteedList) ? guaranteedList.map(g => ({
        cabinno: g.cabinno || 'GTY',
        location: 'Guaranteed',
        cabingrade: g.cabingrade,
        guaranteed: g.guaranteed || 'Y',
        farecode: g.farecode,
        resultno: g.resultno
      })) : []
    }];

    const merged = [...deckList, ...gtyDeck];
    return sortDecksHighToLow(merged);
  }, [roomDetail]);

  // 선택 상태 (모바일)
  const firstIdStr = deckListWithGTY[0]?.id != null ? String(deckListWithGTY[0].id) : '';
  const [selectedFloor, setSelectedFloor] = useState(firstIdStr);
  const [selectedCabin, setSelectedCabin] = useState({ cabinno: 'ALL', location: '모두보기' });

  // 데이터가 바뀌면 초기 선택값 갱신
  // 실제 deck 목록이 바뀐 경우에만 기본 선택값 초기화
  useEffect(() => {
    const first = deckListWithGTY[0];
    const idStr = first?.id != null ? String(first.id) : '';
    setSelectedFloor(idStr);
    // 모바일 요구사항에 맞춰 기본값은 '모두보기'
    setSelectedCabin({ cabinno: 'ALL', location: '모두보기' });
  }, [deckListWithGTY]); // deckList 변경 시

  // 모바일 판별
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 652);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isMobile) {
      const footerEl = document.querySelector('.footer');
      if (!footerEl) return;

      const container = document.createElement('div');
      footerEl.parentNode.insertBefore(container, footerEl.nextSibling);
      setPortalContainer(container);

      // This cleanup function will run when isMobile becomes false
      return () => {
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
        setPortalContainer(null);
      };
    }
  }, [isMobile]);

  useLayoutEffect(() => {
    if (portalContainer && isMobile) {
      const wrapper = portalContainer.firstElementChild;
      if (wrapper) {
        const h = wrapper.getBoundingClientRect().height;
        portalContainer.style.paddingTop = `${h}px`;
      }
    }
  }, [portalContainer, isMobile]);

  // 예약하기
  const handleReservePC = (cabin) => {
    onNextStep({ userSelectCabinInfo: cabin });
  };
  const handleReserveMO = () => {
    if (!selectedCabin?.cabinno) return;
    onNextStep({ userSelectCabinInfo: selectedCabin });
  };

  // 모바일 고정 버튼
  // 모바일 예약 버튼 비활성화 조건: 층 미선택, ALL 선택, 해당 층에 예약 가능 선실 0개, 유효 선실 미선택
  const selectedDeckObj = deckListWithGTY.find((d) => String(d.id) === String(selectedFloor));
  const selectedDeckUniqueCount = Array.isArray(selectedDeckObj?.cabins)
    ? selectedDeckObj.cabins.filter((c, i, a) => a.findIndex(x => x.cabinno === c.cabinno && x.location === c.location) === i).length
    : 0;
  const isAllSelected = String(selectedCabin?.cabinno).toUpperCase() === 'ALL';

  const fixedBtn = (
    <div
      className="depth_2-suggest-btn-wrapper"
      ref={(el) => {
        if (el && portalContainer && portalContainer.firstElementChild !== el) {
          // no-op: 위치 확보용
          setTimeout(() => {});
        }
      }}
    >
      <button
        type="button"
        className="depth_2-suggest-btn"
        onClick={handleReserveMO}
        disabled={!selectedFloor || isAllSelected || selectedDeckUniqueCount === 0 || !selectedCabin?.cabinno}
      >
        예약하기
      </button>
    </div>
  );

  return (
    <>
      {isMobile ? (
        <CarbinTabMO
          data={deckListWithGTY}
          selectedFloor={selectedFloor}
          setSelectedFloor={setSelectedFloor}
          selectedCabin={selectedCabin}
          setSelectedCabin={setSelectedCabin}
        />
      ) : (
        <CarbinTabPC data={deckListWithGTY} onReserve={handleReservePC} />
      )}

      {isMobile && portalContainer && ReactDOM.createPortal(fixedBtn, portalContainer)}
    </>
  );
}

export default GuestRoomBook;
